import { useEffect, useState } from 'react';
import Icon from '../AppIcon';
import { LEGAL_DOCUMENT_BY_ID } from '../../constants/legalDocuments';
import LegalDocumentContent from './LegalDocumentContent';

const DOC_ACCENTS = {
  privacy: 'from-blue-500/15 to-indigo-500/10',
  terms: 'from-violet-500/15 to-purple-500/10',
  cookies: 'from-amber-500/15 to-orange-500/10',
  dpa: 'from-emerald-500/15 to-teal-500/10',
};

const LegalDocumentModal = ({ documentId, onClose }) => {
  const document = LEGAL_DOCUMENT_BY_ID[documentId];
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!document) return null;

  const accent = DOC_ACCENTS[documentId] || DOC_ACCENTS.terms;

  return (
    <div
      className={`fixed inset-0 z-[1100] flex items-center justify-center p-4 sm:p-6 transition-all duration-300 ${
        isVisible ? 'bg-black/55 backdrop-blur-md' : 'bg-black/0'
      }`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-3xl max-h-[88vh] bg-card border border-border/80 rounded-2xl shadow-2xl shadow-black/10 overflow-hidden flex flex-col transition-all duration-300 ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-[0.97]'
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-doc-title"
      >
        <div className={`relative px-6 py-5 border-b border-border/60 bg-gradient-to-br ${accent}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background/70 backdrop-blur-sm border border-border/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Icon name="FileText" size={10} className="text-primary" />
                  Legal
                </span>
                <span className="text-[11px] text-muted-foreground">
                  Effective {document.effectiveDate}
                </span>
              </div>
              <h2 id="legal-doc-title" className="text-xl font-bold text-foreground tracking-tight">
                {document.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-background/60 border border-transparent hover:border-border/50 transition-all"
              aria-label="Close"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-6 sm:px-8 sm:py-7 bg-gradient-to-b from-card to-surface/20">
          <LegalDocumentContent document={document} />
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentModal;
